type Size = 'xs' | 'sm' | 'md' | 'lg';
type Tone = 'light' | 'dark';

const SIZE_MAP: Record<Size, { box: string; img: string; gap: string; text: string; sub: string }> = {
  xs: { box: 'w-7 h-7', img: 'w-6 h-6', gap: 'gap-2', text: 'text-[14px]', sub: 'text-[9.5px]' },
  sm: { box: 'w-8 h-8', img: 'w-7 h-7', gap: 'gap-2.5', text: 'text-[15px]', sub: 'text-[10px]' },
  md: { box: 'w-10 h-10', img: 'w-9 h-9', gap: 'gap-3', text: 'text-[18px]', sub: 'text-[10.5px]' },
  lg: { box: 'w-12 h-12', img: 'w-11 h-11', gap: 'gap-3.5', text: 'text-[22px]', sub: 'text-[11px]' },
};

/** The Jewelitt diamond-"J" brand mark (identical to the Vault topbar). */
const MARK = `
  <defs>
    <linearGradient id="jlSilver" x1="0" y1="0" x2=".6" y2="1"><stop offset="0" stop-color="#F5F6F7"/><stop offset=".5" stop-color="#C4C9CF"/><stop offset="1" stop-color="#8F959D"/></linearGradient>
    <linearGradient id="jlGoldE" x1="0" y1="0" x2=".5" y2="1"><stop offset="0" stop-color="#E9CF95"/><stop offset=".55" stop-color="#C7A05C"/><stop offset="1" stop-color="#93703A"/></linearGradient>
    <linearGradient id="jlGoldJ" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#E7CD92"/><stop offset=".45" stop-color="#C4A163"/><stop offset="1" stop-color="#8F6D38"/></linearGradient>
  </defs>
  <g fill="none" stroke-linejoin="round" stroke-linecap="round">
    <path d="M42,196 L170,64 H470 L598,196 L320,546 Z" stroke="url(#jlSilver)" stroke-width="16"/>
    <path d="M84,202 L196,94 H444 L556,202 L320,496 Z" stroke="url(#jlGoldE)" stroke-width="8" opacity=".9"/>
    <path d="M42,196 H232 M408,196 H598" stroke="url(#jlSilver)" stroke-width="14"/>
    <path d="M170,64 L262,196 L186,292" stroke="url(#jlSilver)" stroke-width="11"/>
    <path d="M470,64 L378,196 L454,292" stroke="url(#jlSilver)" stroke-width="11"/>
  </g>
  <text x="330" y="428" text-anchor="middle" font-family="Times New Roman,Times,serif" font-size="460" font-weight="bold" fill="url(#jlGoldJ)">J</text>`;

export interface LogoProps {
  size?: Size;
  tone?: Tone;
  showWordmark?: boolean;
  showTagline?: boolean;
  tagline?: string;
  className?: string;
}

export default function Logo({
  size = 'sm',
  tone = 'light',
  showWordmark = true,
  showTagline = false,
  tagline = 'Vault · 3D Studio',
  className = '',
}: LogoProps) {
  const s = SIZE_MAP[size];
  const isDark = tone === 'dark';

  return (
    <div className={`inline-flex items-center ${s.gap} ${className}`}>
      <span
        data-no-media-viewer
        className={`relative grid place-items-center ${s.box} shrink-0`}
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 640 570"
          className={`${s.img} select-none pointer-events-none`}
          dangerouslySetInnerHTML={{ __html: MARK }}
        />
      </span>

      {showWordmark && (
        <span className="leading-tight min-w-0">
          <span
            className={`block font-headline font-normal ${s.text} tracking-[-0.025em] ${
              isDark ? 'text-white' : 'text-ink-900'
            }`}
          >
            Jewelitt
          </span>
          {showTagline && (
            <span
              className={`block ${s.sub} mt-0.5 uppercase tracking-[0.22em] font-semibold ${
                isDark ? 'text-white/55' : 'text-ink-500'
              }`}
            >
              {tagline}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
